<template lang="pug">
.mb-10.mt-20.w-full(ref="el")
  .relative.p-2
    .timeline__line.absolute.h-full.bg-gray-200(
      class="md_left-1/2 w-[0.2rem] ml-[-0.1rem]"
    )
    .timeline__step.clear-both.flow-root.relative(
      v-for="(step, i) in screen.steps"
    )
      .timeline__pings.w-4.h-4.block.relative.z-0.-mb-4.-top-2.md_mx-auto(
        :class="i > 0 && 'mt-16'"
        class="ml-[-0.5rem]"
        v-if="step.title || step.img"
      )
        .ping(
          :class="{ 'ping--active': activePing === i }"
        ) 
        .ping(
          :class="{ 'ping--active-2': activePing === i }"
        ) 
        .ping(
          :class="{ 'ping--active-3': activePing === i }"
        ) 
        .w-4.h-4.rounded-full.bg-primary-400.border-4.border-white.absolute.top-0.left-0
      .timeline__grid.mb-4.ms-10.md_ms-0.md_grid.grid-cols-1.md_grid-cols-2.md_gap-20
        .timeline__text.flex.flex-col.row-start-1(
          :class="i % 2 ? 'col-start-1 items-start md_items-end' : 'col-start-2 items-start'"
        )
          uc-editable.fade-in.subheading.pb-2.mb-6.-mt-3.inline-block(
            :editPath="`${editPath}.steps.${i}.title`"
            :k="`${step.id}.title`"
            :text="step.title"
            type="h4"
            v-if="step.title"
          ) 
          div(
            :class="twMerge('p-4 px-6 bg-paper mb-10 rounded', i % 2 ? 'md_slide-from-left slide-from-right' : 'slide-from-right', step.dark && 'dark', step.style)"
            v-if="step.text"
          )
            uc-editable(
              :editPath="`${editPath}.steps.${i}.text`"
              :k="`${step.id}.text`"
              :text="step.text"
            )

        .timeline__image.row-start-1.flex(
          :class="[i % 2 ? 'col-start-2' : 'col-start-1', step['center-y'] ? 'items-center' : 'items-start', step['center-x'] === false ? (i % 2 ? 'justify-start md_justify-start' : 'justify-start md_justify-end') : 'justify-center']"
        )
          uc-asset.py-5.slide-up(
            :class="[{ 'md_-mt-10 mb-10': !step['center-y'] }]"
            :size="step['img-size'] || 24"
            :src="step.img"
            :editPath="`${editPath}.steps.${i}.img`"
            v-if="step.img"
          )
</template>

<script setup lang="ts">
import ScrollTrigger from 'gsap/ScrollTrigger';
import {
  ref,
  watchEffect,
  watch,
  inject,
  toRef,
  withDefaults,
  onMounted,
  nextTick
} from 'vue';
import type { Ref } from 'vue';
import twMerge from '../lib/mergeStyles';
import {
  play,
  stop,
  clearQueue,
  nowPlaying,
  playNextInQueue,
  whenCurrentEnds
} from '../lib/audio';
import useScreenAudio from '../lib/useScreenAudio';
import timelineSchema from '../lib/screen-schemas/screen-timeline.schema';
import { useEventListener } from '@vueuse/core';

const props = withDefaults(
  defineProps<{
    editPath: string;
    screen: UcScreen;
    /** Ancestor owns section intro when nested under `uc-screen` / `templates[]`. */
    nested?: boolean;
  }>(),
  { nested: false }
);
const el = ref(null);
const activePing = ref(0);
const { playSlot, queueSlot } = useScreenAudio(
  toRef(props, 'screen'),
  timelineSchema
);

const sectionActive = inject<Ref<boolean>>('sectionActive', ref(false));

// Prevents scroll-triggered steps from firing audio while the intro is still
// starting up (URL resolving) or before the section has first activated.
const readyForStepAudio = ref(false);

// Intro plays only on the first activation; subsequent section re-entries
// (scroll away and back) skip it and resume at the scroll position.
const hasPlayedIntro = ref(false);

// Plain array (not reactive) — inspected inside the sectionActive watcher to
// determine which trigger positions are already past their start on activation.
const triggerInstances: ScrollTrigger[] = [];

const playSectionAudio = async () => {
  if (!hasPlayedIntro.value) {
    hasPlayedIntro.value = true;

    // await play() is now called inside playSlot, so after this resolves
    // activeId is set and nowPlaying() reliably reflects the intro state.
    await playSlot('intro');

    // Queue every step whose scroll-trigger start was already passed on
    // activation. They will play automatically via playNextInQueue() once
    // the intro ends. Awaiting each call ensures serial URL resolution so
    // items are added to the audio queue in the correct index order.
    let hasCatchup = false;
    for (let i = 0; i < triggerInstances.length; i++) {
      if (triggerInstances[i].progress > 0) {
        await queueSlot(`step-${i}`);
        hasCatchup = true;
      }
    }

    // Hold readyForStepAudio false until the intro actually ENDS.
    // This prevents any step that scrolls into the active zone while the
    // intro is still playing from interrupting it.
    // If no intro audio was available, kick the catch-up queue manually.
    if (nowPlaying()) {
      await whenCurrentEnds();
    } else if (hasCatchup) {
      playNextInQueue();
    }
  }

  // Guard against the section going inactive while we were awaiting.
  if (!sectionActive.value) return;

  readyForStepAudio.value = true;
  console.log('readyForStepAudio', readyForStepAudio.value);
};

watch(sectionActive, active => {
  readyForStepAudio.value = false;
  if (props.nested) return;
  if (!active) return;
  void playSectionAudio();
});

// The first section on a page never receives a false→true sectionActive
// transition, so play its intro here (mirrors uc-screen.vue).
onMounted(async () => {
  await nextTick();
  if (!props.nested && sectionActive.value) void playSectionAudio();
});

// we only reset the audio intro and step audio state when the user scrolls back up and away from the section and then enters again
useEventListener(window, 'scrollsectionleaveback', async e => {
  const currentSection = e.target as HTMLElement;

  if (currentSection?.id === props.screen.id) {
    console.log('scrollsectionleaveback', currentSection?.id);
    readyForStepAudio.value = false;
    hasPlayedIntro.value = false;
  }
});

// Tier 1 (step.audio/speech) → play directly; otherwise use schema slot.
// clearQueue() discards any remaining initial-sequence items before playing
// the manually-triggered step so they don't bleed through afterwards.
const playStep = (i: number) => {
  clearQueue();
  const step = props.screen.steps?.[i] as
    | { audio?: string; speech?: string }
    | undefined;
  const propAudio = step?.audio || step?.speech;
  if (propAudio) play(propAudio);
  else playSlot(`step-${i}`);
};

watchEffect(
  onCleanup => {
    if (!el.value) return;

    triggerInstances.length = 0;

    const tlSteps = Array.from(
      el.value.querySelectorAll('.timeline__step')
    ) as HTMLElement[];
    const scroller = el.value.closest('.scroll-page__content') as HTMLElement;

    tlSteps.forEach((step, i) => {
      const st = ScrollTrigger.create({
        trigger: step,
        scroller,
        start: 'top 50%',
        end: 'bottom 50%',
        onEnter() {
          activePing.value = i;
          if (!readyForStepAudio.value) return;
          playStep(i);
        },
        onEnterBack() {
          // Play audio when scrolling UP through the timeline so the user
          // hears each step as they review in reverse.
          activePing.value = i;
          if (!readyForStepAudio.value) return;
          playStep(i);
        },
        onLeave() {
          if (!readyForStepAudio.value) return;
          stop();
        },
        onLeaveBack() {
          if (!readyForStepAudio.value) return;
          stop();
        }
      });
      triggerInstances.push(st);
    });

    onCleanup(() => {
      triggerInstances.forEach(st => st.kill());
      triggerInstances.length = 0;
    });
  },
  { flush: 'post' }
);
</script>

<style scoped>
.ping {
  transform: scale(0);
  opacity: 0;

  @apply bg-primary-400/50 w-10 h-10 rounded-full absolute -top-3 -left-3 -z-1;
}

.ping--active {
  animation: tlping 4.5s cubic-bezier(0, 0, 0.2, 1) 0s infinite;
  opacity: 1;

  @media screen(md) {
    animation: med_tlping 4.5s cubic-bezier(0, 0, 0.2, 1) 0s infinite;
  }
}

.ping--active-2 {
  animation: tlping 4.5s cubic-bezier(0, 0, 0.2, 1) 1.5s infinite;
  opacity: 1;

  @media screen(md) {
    animation: med_tlping 4.5s cubic-bezier(0, 0, 0.2, 1) 1.5s infinite;
  }
}

.ping--active-3 {
  animation: tlping 4.5s cubic-bezier(0, 0, 0.2, 1) 3s infinite;
  opacity: 1;

  @media screen(md) {
    animation: med_tlping 4.5s cubic-bezier(0, 0, 0.2, 1) 3s infinite;
  }
}

/**
 * we use a smaller max scale on the ping anims on mobile to prevent the anim extending off the screen */
@keyframes tlping {
  0% {
    transform: scale(0);
  }

  100% {
    transform: scale(1.5);
    opacity: 0;
  }
}

@keyframes med_tlping {
  0% {
    transform: scale(0);
  }

  100% {
    transform: scale(3);
    opacity: 0;
  }
}
</style>
